import { isGuideTourPageScrollLocked } from './scrollLock'
import {
  isGuideTourTargetVisibleInViewport,
  syncGuideTourTargetInView,
  type ViewportSize,
} from './targetVisibility'
import type { SpotlightBounds } from './utils'
import type { MeasureGuideTourTargetInput } from './guideTourTargetMeasurement'

export type GuideTourTargetMeasurementContext = {
  element: HTMLElement
  nextBounds: SpotlightBounds
  viewport: ViewportSize
  hasMeasurableSize: boolean
  isInsideActiveModal: boolean
  isVisible: boolean
  positionDrifted: boolean
}

const commitRevealedBounds = (
  input: MeasureGuideTourTargetInput,
  nextBounds: SpotlightBounds
): void => {
  input.stopScrollPolling()
  input.setBounds(nextBounds)
  input.state.hasRevealedBounds = true
  input.state.lastRevealedBounds = nextBounds
}

export const trySyncRevealedTargetWhileScrollLocked = (
  input: MeasureGuideTourTargetInput,
  context: GuideTourTargetMeasurementContext
): boolean => {
  const {
    element,
    nextBounds,
    isInsideActiveModal,
    isVisible,
    positionDrifted,
  } = context

  if (
    !input.getHasRevealedBounds() ||
    isInsideActiveModal ||
    !isGuideTourPageScrollLocked() ||
    (isVisible && !positionDrifted)
  ) {
    return false
  }

  input.keepTargetInViewWhileLocked(element)
  input.state.hasRevealedBounds = true
  input.state.lastRevealedBounds = nextBounds
  return true
}

export const handleGuideTourTargetNotVisible = (
  input: MeasureGuideTourTargetInput,
  context: GuideTourTargetMeasurementContext
): boolean => {
  const {
    element,
    nextBounds,
    isInsideActiveModal,
    isVisible,
    hasMeasurableSize,
  } = context

  if (isVisible || isInsideActiveModal) {
    return false
  }

  if (input.forceScrollToTarget) {
    input.state.isAwaitingReveal = false
    input.requestProgrammaticScroll(element)
  } else if (isGuideTourPageScrollLocked()) {
    syncGuideTourTargetInView(element, { forcePageScroll: false })
    input.startScrollPolling()
  }

  if (
    input.forceScrollToTarget &&
    hasMeasurableSize &&
    input.state.scrollAttempt >= input.maxScrollAttempts
  ) {
    commitRevealedBounds(input, nextBounds)
    return true
  }

  input.setBounds(null)
  return true
}

export const handleGuideTourPendingScrollReveal = (
  input: MeasureGuideTourTargetInput
): boolean => {
  if (!input.state.pendingScrollReveal) {
    return false
  }

  input.stopScrollPolling()

  if (!input.state.isAwaitingReveal) {
    input.state.isAwaitingReveal = true
    input.scheduleRevealAfterScroll({ aborted: false })
  }

  if (!input.state.hasRevealedBounds) {
    input.setBounds(null)
  }

  return true
}

export const tryWaitForGuideTourInitialLayoutStability = (
  input: MeasureGuideTourTargetInput,
  context: GuideTourTargetMeasurementContext,
  waitForInitialStability: (element: HTMLElement) => void
): boolean => {
  const { element } = context

  if (
    !input.forceScrollToTarget ||
    !input.scrollTargetId ||
    input.state.hasRevealedBounds ||
    input.state.initialStabilityDone
  ) {
    return false
  }

  waitForInitialStability(element)
  input.setBounds(null)
  return true
}

export const commitGuideTourVisibleTargetBounds = (
  input: MeasureGuideTourTargetInput,
  nextBounds: SpotlightBounds
): void => {
  commitRevealedBounds(input, nextBounds)
}

export const isGuideTourTargetVisibleForMeasurement = (
  nextBounds: SpotlightBounds,
  viewport: ViewportSize
): boolean => isGuideTourTargetVisibleInViewport(nextBounds, viewport)
